// Core calculation function for distance between two points
const calculateDistance = (p1, p2) => {
    if (!p1 || !p2) return 0;
    
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (p1.lat * Math.PI) / 180;
    const φ2 = (p2.lat * Math.PI) / 180;
    const Δφ = ((p2.lat - p1.lat) * Math.PI) / 180;
    const Δλ = ((p2.lng - p1.lng) * Math.PI) / 180;
  
    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
    return R * c;
  };
  
  const determineVehicleDirection = ({
    currentPosition,
    previousPositions = [],
    stops = [],
    terminalThreshold = 50,
    movementThreshold = 5,
    requiredConfidence = 0.6
  }) => {
    // Input validation
    if (!currentPosition || !stops.length) {
      return {
        isInbound: false,
        confidence: 0,
        terminalReached: null,
        isMoving: false
      };
    }
  
    // Define terminals
    const source = {
      lat: parseFloat(stops[0].latitude),
      lng: parseFloat(stops[0].longitude)
    };
    
    const destination = {
      lat: parseFloat(stops[stops.length - 1].latitude),
      lng: parseFloat(stops[stops.length - 1].longitude)
    };
  
    // Check if vehicle has reached a terminal
    const distanceToSource = calculateDistance(currentPosition, source);
    const distanceToDestination = calculateDistance(currentPosition, destination);
    
    let terminalReached = null;
    if (distanceToDestination < terminalThreshold) {
      terminalReached = 'destination';
    } else if (distanceToSource < terminalThreshold) {
      terminalReached = 'source';
    }
  
    // Analyze movement pattern if we have historical positions
    let movementAnalysis = {
      towardsSource: 0,
      towardsDestination: 0,
      totalMovements: 0
    };
  
    if (previousPositions.length > 0) {
      // Include current position in analysis
      const positionsToAnalyze = [...previousPositions, currentPosition];
      
      for (let i = 1; i < positionsToAnalyze.length; i++) {
        const prev = positionsToAnalyze[i - 1];
        const curr = positionsToAnalyze[i];
  
        const prevToSource = calculateDistance(prev, source);
        const prevToDest = calculateDistance(prev, destination);
        const currToSource = calculateDistance(curr, source);
        const currToDest = calculateDistance(curr, destination);
  
        // Check if movement is significant enough to count
        const movementDistance = calculateDistance(prev, curr);
        if (movementDistance > movementThreshold) {
          movementAnalysis.totalMovements++;
          
          if (currToSource < prevToSource && currToDest > prevToDest) {
            movementAnalysis.towardsSource++;
          } else if (currToDest < prevToDest && currToSource > prevToSource) {
            movementAnalysis.towardsDestination++;
          }
        }
      }
    }
  
    // Calculate confidence and determine direction
    let isInbound = false;
    let confidence = 0;
  
    if (movementAnalysis.totalMovements > 0) {
      const sourceConfidence = movementAnalysis.towardsSource / movementAnalysis.totalMovements;
      const destConfidence = movementAnalysis.towardsDestination / movementAnalysis.totalMovements;
      
      if (sourceConfidence > requiredConfidence) {
        isInbound = true;
        confidence = sourceConfidence;
      } else if (destConfidence > requiredConfidence) {
        isInbound = false;
        confidence = destConfidence;
      } else {
        // Fallback to position-based determination if movement pattern is unclear
        const totalRouteDistance = calculateDistance(source, destination);
        const currentToSource = calculateDistance(currentPosition, source);
        isInbound = currentToSource > totalRouteDistance / 2;
        confidence = 0.5; // Lower confidence for position-based determination
      }
    }
  
    // Override based on terminal reached
    if (terminalReached === 'destination') {
      isInbound = true;
      confidence = 1;
    } else if (terminalReached === 'source') {
      isInbound = false;
      confidence = 1;
    }
  
    return {
      isInbound,
      confidence,
      terminalReached,
      isMoving: movementAnalysis.totalMovements > 0,
      analytics: {
        ...movementAnalysis,
        distanceToSource,
        distanceToDestination
      }
    };
  };
  
  export { determineVehicleDirection, calculateDistance };